import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { plantsAPI } from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import ThemeToggle from '../components/ThemeToggle';
import ReportBugButton from '../components/ReportBugButton';
import './PlantDetail.css';

function PlantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPlant = async () => {
      try {
        setLoading(true);
        const response = await plantsAPI.getOne(id);
        setPlant(response.data);
        setError('');
      } catch (err) {
        setError('Failed to load plant details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlant();
  }, [id]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this plant?')) {
      try {
        await plantsAPI.delete(id);
        navigate('/dashboard');
      } catch (err) {
        setError('Failed to delete plant.');
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading plant details...</div>;
  }

  if (error || !plant) {
    return (
      <div className="error-container">
        <p>{error || 'Plant not found'}</p>
        <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="plant-detail">
      <ReportBugButton />
      <header className="detail-header">
        <div className="detail-header-left">
          <button onClick={() => navigate('/dashboard')} className="btn-back">
            ← Back
          </button>
          <h1>{plant.name}</h1>
        </div>
        <ThemeToggle />
      </header>

      <div className="detail-content">
        <div className="plant-image-container">
          <img 
            src={plant.imageUrl} 
            alt={plant.name}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=Plant+Image';
            }}
          />
          {plant.favorite && <span className="favorite-badge">⭐ Favorite</span>}
        </div>

        <div className="plant-info">
          <div className="info-section">
            <h2>Basic Information</h2>
            <div className="info-grid">
              {plant.species && (
                <div className="info-item">
                  <span className="info-label">Species:</span>
                  <span className="info-value">{plant.species}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Category:</span>
                <span className="info-value">{plant.category}</span>
              </div>
              {plant.acquisitionDate && (
                <div className="info-item">
                  <span className="info-label">Acquired:</span>
                  <span className="info-value">
                    {formatDate(plant.acquisitionDate)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {plant.tags && plant.tags.length > 0 && (
            <div className="info-section">
              <h2>Tags</h2>
              <div className="plant-tags-detail">
                {plant.tags.map((tag, index) => (
                  <span key={index} className="tag-badge-detail">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {plant.careNotes && (
            <div className="info-section">
              <h2>🌱 Care Tips</h2>
              <p className="info-text">{plant.careNotes}</p>
            </div>
          )}

          {plant.soilMixNotes && (
            <div className="info-section">
              <h2>🪴 Soil Mix</h2>
              <p className="info-text">{plant.soilMixNotes}</p>
            </div>
          )}

          <div className="action-buttons">
            <button 
              onClick={() => navigate(`/plants/${id}/edit`)} 
              className="btn btn-primary"
            >
              Edit Plant
            </button>
            <button 
              onClick={handleDelete} 
              className="btn btn-danger"
            >
              Delete Plant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlantDetail;
